import axios from 'axios'

const api = axios.create({
  baseURL: '/api',
})

export const startLogin = async () => {
  const response = await api.get('/auth')
  return response.data
}

export const getSession = async oauthToken => {
  const response = await api.get(`/auth/session/${oauthToken}`)
  return response.data
}

export const saveUserSavedFilters = async ({ oauthToken, savedFilters }) => {
  const response = await api.put(`/auth/session/${oauthToken}/saved-filters`, {
    savedFilters,
  })
  return response.data
}

export const runSync = async oauthToken => {
  const response = await api.post('/sync/femmeleague', { oauthToken })
  return response.data
}

export const runFlagSync = async oauthToken => {
  const response = await api.post('/sync/flags', { oauthToken })
  return response.data
}

export const runPlayerSync = async oauthToken => {
  const response = await api.post('/sync/players', { oauthToken })
  return response.data
}

export const runOldPlayerDetailsSync = async oauthToken => {
  const response = await api.post('/sync/players/import-old-details-from-backup', { oauthToken })
  return response.data
}

export const runMatchSync = async oauthToken => {
  const response = await api.post('/sync/matches', { oauthToken })
  return response.data
}

export const runMatchForceSync = async oauthToken => {
  const response = await api.post('/sync/matches/force', { oauthToken })
  return response.data
}

export const runBotCleanupSync = async oauthToken => {
  const response = await api.post('/sync/cleanup/bots', { oauthToken })
  return response.data
}

export const runCountrySync = async oauthToken => {
  const response = await api.post('/countries/sync', { oauthToken })
  return response.data
}

export const getLatestSuccessfulTeamUpdate = async () => {
  const response = await api.get('/updates/latest-success')
  return response.data
}

export const getActiveUpdate = async () => {
  const response = await api.get('/updates/active')
  return response.data
}

export const getBotSeries = async level => {
  const params = {}

  if (level === 'VII' || level === 7) {
    params.level = 'VII'
  }

  const response = await api.get('/teams/bot-series', { params })
  return response.data
}

export const getTeams = async (countryId, limit, specialtyMode, options = {}) => {
  const params = {}

  if (Number.isInteger(countryId) && countryId > 0) {
    params.countryId = countryId
  }

  if (Number.isInteger(limit) && limit > 0) {
    params.limit = limit
  }

  if (specialtyMode === 'plus' || specialtyMode === 'minus') {
    params.specialtyMode = specialtyMode
  }

  if (options.discordNo === true) {
    params.discordNo = 'true'
  }

  if (options.favorite === true) {
    params.favorite = 'true'
  }

  if (Number.isInteger(options.seriesId) && options.seriesId > 0) {
    params.seriesId = options.seriesId
  }

  if (options.seriesName) {
    params.seriesName = String(options.seriesName)
  }

  if (options.maxYouth === true) {
    params.maxYouth = 'true'
  }

  if (options.oauthToken) {
    params.oauthToken = options.oauthToken
  }

  if (options.countryScope === 'team' || options.countryScope === 'user') {
    params.countryScope = options.countryScope
  }

  if (options.sortBy) {
    params.sortBy = options.sortBy
  }

  if (options.sortDirection === 'asc' || options.sortDirection === 'desc') {
    params.sortDirection = options.sortDirection
  }

  if (options.playerStatsScope === 'all' || options.playerStatsScope === 'top11') {
    params.playerStatsScope = options.playerStatsScope
  }

  const response = await api.get('/teams', { params })
  return response.data
}

export const getFilteredTeams = async filters => {
  const params = {}
  const safeFilters = filters || {}

  const setParam = (key, value) => {
    if (value === undefined || value === null || value === '') {
      return
    }

    if (Array.isArray(value)) {
      const normalizedValues = value.map(item => `${item ?? ''}`.trim()).filter(Boolean)

      if (!normalizedValues.length) {
        return
      }

      params[key] = normalizedValues.join(',')
      return
    }

    params[key] = value
  }

  setParam('userCountryId', safeFilters.userCountryId)
  setParam('teamCountryId', safeFilters.teamCountryId)
  setParam('cup', safeFilters.cup)
  setParam('coachSkillMin', safeFilters.coachSkillMin)
  setParam('coachSkillMax', safeFilters.coachSkillMax)
  setParam('coachLeadershipMin', safeFilters.coachLeadershipMin)
  setParam('coachLeadershipMax', safeFilters.coachLeadershipMax)
  setParam('coachType', safeFilters.coachType)
  setParam('youth', safeFilters.youth)
  setParam('fansMin', safeFilters.fansMin)
  setParam('fansMax', safeFilters.fansMax)
  setParam('arenaMin', safeFilters.arenaMin)
  setParam('arenaMax', safeFilters.arenaMax)
  setParam('hatstatsMin', safeFilters.hatstatsMin)
  setParam('hatstatsMax', safeFilters.hatstatsMax)
  setParam('ptsMin', safeFilters.ptsMin)
  setParam('ptsMax', safeFilters.ptsMax)
  setParam('posMin', safeFilters.posMin)
  setParam('posMax', safeFilters.posMax)
  setParam('seriesLevel', safeFilters.seriesLevel)
  setParam('limit', safeFilters.filterLimit)
  setParam('discord', safeFilters.discord)

  const response = await api.get('/teams/filter', { params })
  return response.data
}

export const getFavoriteTeamIds = async oauthToken => {
  const response = await api.get('/teams/favorites', {
    params: { oauthToken },
  })
  return response.data
}

export const getMyCup = async ({ oauthToken, teamId } = {}) => {
  const response = await api.get('/teams/my-cup', {
    params: { oauthToken, teamId },
  })
  return response.data
}

export const getAverageTeams = async ({ oauthToken, playerStatsScope } = {}) => {
  const params = {}

  if (oauthToken) {
    params.oauthToken = oauthToken
  }

  if (playerStatsScope === 'all' || playerStatsScope === 'top11') {
    params.playerStatsScope = playerStatsScope
  }

  const response = await api.get('/teams/average', { params })
  return response.data
}

export const searchMyCupTeams = async term => {
  const response = await api.get('/teams/search', { params: { term } })
  return response.data
}

export const toggleFavoriteTeam = async ({ oauthToken, teamId }) => {
  const response = await api.post('/teams/favorites/toggle', {
    oauthToken,
    teamId,
  })
  return response.data
}

export const getCountries = async (countryScope = 'user') => {
  const params = {}

  if (countryScope === 'team' || countryScope === 'user') {
    params.countryScope = countryScope
  }

  const response = await api.get('/teams/countries', { params })
  return response.data
}
